
 // src/components/audit/AuditTable.jsx
 // Columns: ID | Severity | Theme | Status | Deadline | Comments
 import React, { useEffect, useMemo, useState } from "react";
 import axios from "axios";
 import { Toast, ToastContainer } from "react-bootstrap";
 import AuditFilters from "./AuditFilters";
 import AuditRow from "./AuditRow";
 import AddFindingModal from "./AddFindingModal.jsx";
 import EditFindingModal from "./EditFindingModal.jsx";
 import ImportCsvModal from "./ImportCsvModal.jsx";
 import "./AuditView.css";
 import AuditStats from "./AuditStats";
 import AuditRanking from "./AuditRanking";

 export default function AuditTable({ user, userCountryCode }) {
   const isAdmin =
     user?.user_type?.toLowerCase() === "admin" ||
     Number(user?.is_admin) === 1;

   const userCountry = useMemo(() => {
     const raw = user?.location ?? userCountryCode ?? "";
     return String(raw).trim().toUpperCase();
   }, [user?.location, userCountryCode]);

   const [selectedCountry, setSelectedCountry] = useState(
     userCountry || "FR"
   );

   // Non-admins always use their assigned country.
   const country = isAdmin ? selectedCountry : userCountry;

   const setCountry = (nextCountry) => {
     if (isAdmin) {
       setSelectedCountry(nextCountry);
     }
   };

   const [year, setYear] = useState(new Date().getFullYear());
   const [items, setItems] = useState([]);
   const [expanded, setExpanded] = useState({});

   const [showModal, setShowModal] = useState(false);
   const [showImport, setShowImport] = useState(false);
   const [showEdit, setShowEdit] = useState(false);
   const [editTarget, setEditTarget] = useState(null);

   const [toast, setToast] = useState({
     show: false,
     message: "",
     variant: "success",
   });

   const showToast = (message, variant = "success") =>
     setToast({ show: true, message, variant });

   // Initialize the admin's selection when their country becomes available.
   useEffect(() => {
     if (isAdmin && userCountry) {
       setSelectedCountry((previous) =>
         previous === "FR" ? userCountry : previous
       );
     }
   }, [isAdmin, userCountry]);

   // Fetch audit findings
   const fetchData = async (opts = {}) => {
     const preserveExpanded = !!opts.preserveExpanded;

     if (!country || !year) {
       setItems([]);
       return;
     }

     try {
       const res = await axios.get("/api/audit.php", {
         params: { country, year },
         withCredentials: true,
       });

       if (!preserveExpanded) setExpanded({});
       setItems(res.data?.data || []);
       showToast(
         `Audit data loaded for ${country} (${year})`,
         "success"
       );
     } catch (e) {
       console.error("Failed to load audits", e);
       setItems([]);
       showToast("Failed to load audit findings", "danger");
     }
   };

   useEffect(() => {
     if (country && year) {
       fetchData();
     } else {
       setItems([]);
     }
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [country, year]);

   const toggle = (findingId) =>
     setExpanded((prev) => ({
       ...prev,
       [findingId]: !prev[findingId],
     }));

   const onAdded = (row) => {
     setItems((prev) => [row, ...prev]);
     setShowModal(false);
     showToast("Finding added", "success");
   };

   const openEdit = (row) => {
     setEditTarget(row);
     setShowEdit(true);
   };

   const onUpdated = (updatedRow) => {
     setItems((prev) =>
       prev.map((it) =>
         it.finding_id === (editTarget?.finding_id ?? updatedRow.finding_id)
           ? updatedRow
           : it
       )
     );

     setShowEdit(false);
     setEditTarget(null);
     showToast("Finding updated", "success");
   };

   return (
     <div className="audit-wrapper mx-auto mt-4">
       <AuditFilters
         country={country}
         setCountry={setCountry}
         year={year}
         setYear={setYear}
         onAddFinding={() => setShowModal(true)}
         onImport={() => {
           if (isAdmin) setShowImport(true);
         }}
         user={user}
       />

       <AuditStats country={country} onToast={showToast} />

       {/* Ranking par année */}
       {/* <AuditRanking year={year} onToast={showToast} /> */}

       <div className="audit-table-scroll">
         <table className="table table-dark table-bordered audit-table">
           <colgroup>
             <col className="col-id" />
             <col className="col-sev" />
             <col className="col-theme" />
             <col className="col-status" />
             <col className="col-deadline" />
             <col className="col-comments" />
           </colgroup>

           <thead>
             <tr>
               <th>ID</th>
               <th>Severity</th>
               <th>Theme</th>
               <th>Status</th>
               <th>Deadline</th>
               <th>Comments</th>
             </tr>
           </thead>

           <tbody>
             {items.length ? (
               items.map((row) => (
                 <AuditRow
                   key={row.finding_id}
                   row={row}
                   isExpanded={!!expanded[row.finding_id]}
                   toggle={() => toggle(row.finding_id)}
                   onEditRow={openEdit}
                   onToast={showToast}
                   user={user}
                   onRefresh={fetchData}
                 />
               ))
             ) : (
               <tr>
                 <td colSpan={6} className="audit-empty-cell">
                   No results found.
                 </td>
               </tr>
             )}
           </tbody>
         </table>
       </div>

       <AddFindingModal
         show={showModal}
         onHide={() => setShowModal(false)}
         country={country}
         year={year}
         onAdded={onAdded}
         onToast={showToast}
       />

       <EditFindingModal
         show={showEdit}
         onHide={() => setShowEdit(false)}
         row={editTarget}
         onSaved={onUpdated}
         onToast={showToast}
       />

       <ImportCsvModal
         show={showImport && isAdmin}
         onHide={() => setShowImport(false)}
         country={country}
         year={year}
         onImported={fetchData}
         onToast={showToast}
       />

       <ToastContainer
         position="bottom-end"
         className="p-3"
         style={{
           position: "fixed",
           bottom: 20,
           right: 20,
           zIndex: 9999,
         }}
       >
         <Toast
           show={toast.show}
           bg={toast.variant}
           onClose={() =>
             setToast((p) => ({ ...p, show: false }))
           }
           delay={3000}
           autohide
         >
           <Toast.Body className="text-white">
             {toast.message}
           </Toast.Body>
         </Toast>
       </ToastContainer>
     </div>
   );
 }